import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import type { QueryClient } from '@tanstack/react-query';
import type { DisruptionProgress, ResolutionOp, ResolutionState } from '@/lib/disruptions/types';
import { bundleOf, factsOf, runStatusLabel, shortOperator, type RunSample } from './tower-model';
import { shortHotel } from './tower-transfer-board';
import { towerT, type TowerT } from './tower-i18n';

// LOG (client journal, phase 1) — what happened since this page was opened,
// derived from transitions of the WS-fed progress snapshot + the operator's own
// fires. Per-session and per-browser on purpose (labelled as such in the UI); the
// persisted server feed is phase 3. Also keeps the run SAMPLES (done@time) the
// IN PROGRESS panel turns into a client-side rate/ETA — the BFF exposes neither yet.

export interface JournalLine {
  at: number;
  text: string;
  tone?: 'ok' | 'warn' | 'danger' | 'info' | undefined;
}

export interface PendingFire {
  op: ResolutionOp;
  since: number;
  count: number;
}

interface JournalState {
  lines: Record<string, JournalLine[]>;
  samples: Record<string, RunSample[]>;
  /** A resolve-next POST in flight per disruption (the console sets it, the board reads it). */
  pending: Record<string, PendingFire | null>;
  append: (urn: string, text: string, tone?: JournalLine['tone']) => void;
  sample: (urn: string, runKey: string, done: number) => void;
  resetSamples: (urn: string) => void;
  setPending: (urn: string, fire: PendingFire | null) => void;
}

const MAX_LINES = 60;
const MAX_SAMPLES = 40;

export const useTowerJournal = create<JournalState>((set) => ({
  lines: {},
  samples: {},
  pending: {},
  setPending: (urn, fire) => set((s) => ({ pending: { ...s.pending, [urn]: fire } })),
  append: (urn, text, tone) =>
    set((s) => {
      const prev = s.lines[urn] ?? [];
      // De-dupe an identical consecutive line (WS pushes can repeat a terminal state).
      if (prev[0]?.text === text) return s;
      return {
        lines: { ...s.lines, [urn]: [{ at: Date.now(), text, tone }, ...prev].slice(0, MAX_LINES) },
      };
    }),
  sample: (urn, runKey, done) =>
    set((s) => {
      const key = `${urn}#${runKey}`;
      const prev = s.samples[key] ?? [];
      const last = prev[prev.length - 1];
      if (last && last.done === done) return s;
      return {
        samples: { ...s.samples, [key]: [...prev, { at: Date.now(), done }].slice(-MAX_SAMPLES) },
      };
    }),
  resetSamples: (urn) =>
    set((s) => {
      const next: Record<string, RunSample[]> = {};
      for (const k of Object.keys(s.samples)) if (!k.startsWith(`${urn}#`)) next[k] = s.samples[k]!;
      return { samples: next };
    }),
}));

/** Watches the live snapshot and writes journal lines on transitions. */
export function useJournalFeed(
  urn: string,
  live: DisruptionProgress | undefined,
  state: ResolutionState | undefined,
  t: TowerT = towerT,
): void {
  const append = useTowerJournal((s) => s.append);
  const sample = useTowerJournal((s) => s.sample);
  const prev = useRef<{
    runStatus?: string | undefined;
    runId?: string | null | undefined;
    activeOp?: string | null | undefined;
    allocStatus?: string | undefined;
    contained?: boolean | undefined;
    roomsCommitted?: number | undefined;
    offered?: number | undefined;
    accepted?: number | undefined;
    declined?: number | undefined;
    issued?: number | undefined;
    /** MASS ground transport — per hotel: runs on the road / arrived (from the live snapshot). */
    transferByHotel?: Map<string, { moving: number; arrived: number }> | undefined;
    vehiclesOnRoad?: number | undefined;
    paxArrived?: number | undefined;
  }>({});

  useEffect(() => {
    append(urn, t('journal.opened'), 'info');
    // Once per mount on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urn]);

  useEffect(() => {
    if (!live) return;
    const p = prev.current;
    const activeOp = live.activeRun?.op ?? null;
    const runStatus = live.runStatus;
    const runId = live.runRunId ?? null;

    if (activeOp && activeOp !== p.activeOp) {
      const who = live.activeRun?.operatorUrn
        ? shortOperator(live.activeRun.operatorUrn, t)
        : t('common:holds.lock.anotherOperator');
      append(urn, t('journal.runStarted', { op: opTitle(activeOp, t), who }));
    }
    if (
      p.runStatus &&
      runStatus !== p.runStatus &&
      (runStatus === 'COMPLETED' || runStatus === 'PARTIAL' || runStatus === 'FAILED')
    ) {
      const done = live.processedGroups ?? 0;
      const total = live.plannedGroups ?? 0;
      append(
        urn,
        t('journal.flightSearch', {
          status: runStatusLabel(runStatus, t),
          counter: total ? ` · ${done}/${total}` : '',
          noOptions: live.noOptionGroups
            ? t('journal.withoutOptions', { count: live.noOptionGroups })
            : '',
        }),
        runStatus === 'FAILED' ? 'danger' : runStatus === 'PARTIAL' ? 'warn' : 'ok',
      );
    }
    const alloc = live.allocationRun;
    if (alloc && p.allocStatus && alloc.status !== p.allocStatus && alloc.status !== 'RUNNING') {
      append(
        urn,
        t('journal.hotels', {
          status: runStatusLabel(alloc.status, t),
          held: alloc.heldGroups + alloc.alreadyReservedGroups,
          total: alloc.totalGroups,
          soldOut: alloc.soldOutGroups
            ? t('journal.soldOut', { count: alloc.soldOutGroups })
            : '',
        }),
        alloc.status === 'FAILED' ? 'danger' : 'ok',
      );
    }
    if (p.roomsCommitted != null && (live.roomsCommitted ?? 0) > p.roomsCommitted) {
      append(urn, t('journal.hotelsConfirmed', { count: live.roomsCommitted }), 'ok');
    }
    // Shuttle runs (MASS ground transport): a line per hotel when a run departs
    // to it / arrives at it — the snapshot is bumped by every run push.
    const tr = live.transferRun;
    if (tr) {
      const prevHotels = p.transferByHotel;
      const nextHotels = new Map<string, { moving: number; arrived: number }>();
      for (const h of tr.byHotel) {
        const moving = h.runs.inTransit;
        const arrived = h.runs.arrived;
        nextHotels.set(h.hotelUrn, { moving, arrived });
        const before = prevHotels?.get(h.hotelUrn);
        if (!prevHotels) continue; // first snapshot — no transition to report
        const name = h.hotelName ?? shortHotel(h.hotelUrn);
        if (moving > (before?.moving ?? 0))
          append(
            urn,
            t('journal.shuttleDeparted', {
              hotel: name,
              arrived: h.paxArrived,
              total: h.paxToMove,
            }),
            'info',
          );
        if (arrived > (before?.arrived ?? 0))
          append(
            urn,
            t('journal.shuttleArrived', {
              hotel: name,
              arrived: h.paxArrived,
              total: h.paxToMove,
            }),
            'ok',
          );
      }
      if (p.vehiclesOnRoad === 0 && tr.vehiclesOnRoad > 0)
        append(urn, t('journal.shuttlesDispatched', { count: tr.vehiclesOnRoad }), 'info');
      if (p.vehiclesOnRoad != null && p.vehiclesOnRoad > 0 && tr.vehiclesOnRoad === 0 && tr.paxTotal > 0)
        append(
          urn,
          t('journal.shuttleWaveSettled', { arrived: tr.paxArrived, total: tr.paxTotal }),
          tr.paxArrived >= tr.paxTotal ? 'ok' : 'info',
        );
      p.transferByHotel = nextHotels;
      p.vehiclesOnRoad = tr.vehiclesOnRoad;
      p.paxArrived = tr.paxArrived;
    }
    // Run samples for rate/ETA.
    if (activeOp === 'rebooking')
      sample(urn, `rebooking:${runId ?? 'x'}`, live.processedGroups ?? 0);
    else if (activeOp === 'allocation' && alloc)
      sample(urn, `allocation:${alloc.runId}`, alloc.heldGroups + alloc.alreadyReservedGroups);
    else if (activeOp && live.activeRun?.progress)
      sample(urn, `${activeOp}:${live.activeRun.runId}`, live.activeRun.progress.done);

    prev.current = {
      ...p,
      runStatus,
      runId,
      activeOp,
      allocStatus: alloc?.status,
      contained: p.contained,
      roomsCommitted: live.roomsCommitted ?? 0,
    };
  }, [urn, live, append, sample, t]);

  useEffect(() => {
    if (!state) return;
    const contained = state.steps.find((s) => s.op === 'contain')?.status === 'DONE';
    if (contained && prev.current.contained === false)
      append(urn, t('journal.contained'), 'ok');
    prev.current.contained = contained;
    // Acceptance bundle: offers out · accepted · issued (ticket + hotel) — from the
    // facts (they move on every WS push that re-reads resolution-state).
    const g = factsOf(state);
    if (g) {
      const b = bundleOf(g);
      const p = prev.current;
      if (p.offered != null && b.offered > p.offered)
        append(
          urn,
          b.offerReady > 0
            ? t('journal.offersSentOf', { count: b.offered, ready: b.offerReady })
            : t('journal.offersSent', { count: b.offered }),
          'info',
        );
      if (p.accepted != null && g.accepted > p.accepted)
        append(
          urn,
          b.offered > 0
            ? t('journal.acceptedOf', { count: g.accepted, offered: b.offered })
            : t('journal.accepted', { count: g.accepted }),
          'ok',
        );
      if (p.declined != null && g.declined > p.declined)
        append(urn, t('journal.declined', { count: g.declined }), 'warn');
      if (p.issued != null && b.issued > p.issued)
        append(urn, t('journal.issued', { count: b.issued }), 'ok');
      p.offered = b.offered;
      p.accepted = g.accepted;
      p.declined = g.declined;
      p.issued = b.issued;
    }
  }, [urn, state, append, t]);
}

export function opTitle(op: string, t: TowerT = towerT): string {
  switch (op) {
    case 'rebooking':
      return t('journal.op.rebooking');
    case 'allocation':
      return t('journal.op.allocation');
    case 'transfers':
      return t('journal.op.transfers');
    case 'tickets':
    case 'issue':
      return t('journal.op.tickets');
    case 'acceptance':
      return t('journal.op.acceptance');
    case 'vouchers':
      return t('journal.op.vouchers');
    default:
      return op;
  }
}

/** Samples for the run currently in flight (or []). */
export function useRunSamples(urn: string, live: DisruptionProgress | undefined): RunSample[] {
  const samples = useTowerJournal((s) => s.samples);
  const op = live?.activeRun?.op;
  if (!op) return [];
  const key =
    op === 'rebooking'
      ? `${urn}#rebooking:${live?.runRunId ?? 'x'}`
      : op === 'allocation'
        ? `${urn}#allocation:${live?.allocationRun?.runId ?? 'x'}`
        : `${urn}#${op}:${live?.activeRun?.runId ?? 'x'}`;
  return samples[key] ?? NO_SAMPLES;
}

const NO_SAMPLES: RunSample[] = [];

/**
 * A run can start AND finish between two BFF ticker fingerprints (a re-run over
 * already-settled groups takes milliseconds) — the room then never sees a terminal
 * push and the last snapshot the UI fetched may read RUNNING forever. After a fire
 * from the Tower we re-read progress + resolution-state a few times on a bounded
 * back-off (3 s · 10 s · 30 s), then stop. Not a poll: it always ends.
 */
export function scheduleCatchUp(qc: QueryClient, urn: string): () => void {
  const timers = [3_000, 10_000, 30_000].map((ms) =>
    window.setTimeout(() => {
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'progress'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
    }, ms),
  );
  return () => timers.forEach((t) => window.clearTimeout(t));
}
